import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import {
  GetProducts,
  GetProductById,
  DeleteProduct,
} from "../../../services/InventoryService";
import CommonLoader from "../../common/CommonLoader";
import ViewProduct from "./ViewProduct";
import { ReorderStatusChip } from "../tracker/ReorderReport";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import { NotificationManager } from "react-notifications";

const ProductsTable = () => {
  const navigate = useNavigate();

  const columns = [
    "Product",
    "Item Number",
    "UPC",
    "Vendor",
    "Category",
    "Order Level",
    "Cost",
    "Stock",
    "Reorder",
    "Actions",
  ];
  const columnHeaders = [
    "productName",
    "sku",
    "upc",
    "vendorName",
    "category",
    "orderLevel",
  ];
  const searchableHeaders = [
    "productName",
    "sku",
    "upc",
    "vendorName",
    "category",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [productsData, setProductsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [selectedItemToDelete, setSelectedItemToDelete] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const filteredResults = productsData.filter((product) =>
      searchableHeaders.some((header) =>
        product[header]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, productsData]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await GetProducts();
      setProductsData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching products:", error);
      NotificationManager.error("Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddProduct = () => {
    navigate("/vlp/inventory-products/add");
  };

  const handleEditClick = (product) => {
    navigate(`/vlp/inventory-products/${product.id}`);
  };

  // The list only carries the header row, so pull the full record with its
  // variants and locations before showing the detail panel.
  const handleViewClick = async (product) => {
    try {
      setLoading(true);
      const response = await GetProductById(product.id);
      setSelectedProduct(response.results);
    } catch (error) {
      console.error("Error fetching product:", error);
      NotificationManager.error("Error fetching product");
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    setSelectedProduct(null);
    setSearchTerm("");
  };

  const handleDeleteClick = (product) => {
    setSelectedItemToDelete(product);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      await DeleteProduct(selectedItemToDelete.id);
      setIsDeleteConfirmationOpen(false);
      const updatedProducts = productsData.filter(
        (item) => item.id !== selectedItemToDelete.id
      );
      setProductsData(updatedProducts);
      setFilteredData(updatedProducts);
      NotificationManager.success(
        `${selectedItemToDelete.productName} deleted successfully`
      );
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      console.error("Error deleting product:", error);
      // A product still sitting on a supply order or test kit comes back as a 409.
      if (error?.response?.status === 409) {
        NotificationManager.error(
          "This product is used on a supply order or test kit and cannot be deleted"
        );
      } else {
        NotificationManager.error("Error deleting product");
      }
    }
  };

  const renderActions = (product) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        size="small"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(product)}
      >
        <EditIcon fontSize="small" />
      </IconButton>

      <IconButton
        aria-label="view"
        size="small"
        style={{ color: "green" }}
        onClick={() => handleViewClick(product)}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
      <IconButton
        aria-label="delete"
        size="small"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(product)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );

  return (
    <div className="vlp-component-container">
      <span className="title">Products </span>
      <hr />
      {selectedProduct ? (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewProduct product={selectedProduct} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer>
              <Table sx={{ minWidth: 400 }} size="small">
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product, index) => (
                      <TableRow
                        key={product.id}
                        sx={{
                          // ENB-584: products at their reorder point are highlighted.
                          backgroundColor:
                            product.reorder?.reorderStatus === "Order Now"
                              ? "rgba(211, 47, 47, 0.12)"
                              : index % 2 === 0
                              ? "rgba(0, 0, 0, 0.04)"
                              : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>{product[column]}</TableCell>
                        ))}
                        <TableCell>
                          {product.cost !== null && product.cost !== undefined
                            ? `$${Number(product.cost).toFixed(2)}`
                            : ""}
                        </TableCell>
                        <TableCell>{product.currentStockLevel}</TableCell>
                        <TableCell>
                          <ReorderStatusChip status={product.reorder?.reorderStatus} />
                        </TableCell>
                        <TableCell>{renderActions(product)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
        </>
      )}

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedItemToDelete?.productName}?`}
        color="red"
      />
    </div>
  );
};

export default ProductsTable;
